/* eslint-disable */
import React, { useEffect, useState } from 'react'
import { FooterButtons, SingleSelect, TextField, Tip, Title } from 'ui'
import { z } from 'zod'
import { SCHOOL_GOVT_OTHER, YES_NO_DDN } from '~/constants/common'
import { opportunityId } from '~/cookie'
import { useLoaderActionData } from '~/hooks/useLoaderActionData'
import { authenticator } from '~/services/auth.server'
import refreshToken from '~/services/refreshToken.server'
import { badRequest } from '~/utils/badRequest'
import { GenericActionData, clientValidation } from '~/utils/clientValidation'
import { inferSafeParseErrors } from '~/utils/inferSafeParseErrors'
import { preventNonNumericalInput } from '~/utils/preventNonNumericalInput'
import { uniqueId } from '~/utils/uniqueId'

import { Container, Grid, Space } from '@mantine/core'
import { ActionFunction, LoaderFunction, json, redirect } from '@remix-run/node'
import { Form, useActionData, useLocation, useNavigate, useNavigation } from '@remix-run/react'

import { sessionStorage } from '../../services/session.server'
import getInfo from '../api/get-information'
import saveInfo from '../api/save-information'

export const schema = z.object({
  type: z.string({ required_error: 'Please select an option.' }),
  amount: z.string().nonempty('Please enter amount.'),
  additional: z.string().optional(),
  fundingsources: z.string().array().optional(),
  fundType: z.string().optional(),
})

type SchemaFields = z.infer<typeof schema>
type SchemaFieldsErrors = inferSafeParseErrors<typeof schema>
type ActionData = GenericActionData<SchemaFields, SchemaFieldsErrors>

export const action: ActionFunction = async ({ request }) => {
  const formData = await request.formData()
  const fields = Object?.fromEntries(formData.entries()) as unknown as SchemaFields
  const result = schema?.safeParse(fields)
  const session = await sessionStorage.getSession(request.headers.get('cookie'))
  const cookie = (await opportunityId.parse(request.headers.get('Cookie'))) || {}
  if (!cookie?.oppID) return redirect('/dashboard')
  const accessToken = await refreshToken(request, session)
  const fetchData = await getInfo(accessToken, cookie.oppID, 'additional_funding', request)

  let fundingsources = fetchData?.data?.response?.additional_funding?.fundingsources

  if (!result.success) {
    return badRequest({
      fields: { ...fields, fundingsources },
      errors: result.error.flatten(),
      status: false,
      dataType: 'actionData',
    })
  }

  const uniq = uniqueId()
  const obj = {
    type: fields?.fundType,
    amount: Number(fields?.amount),
    provider: fields?.type,
    Deleted: false,
    accountType: '',
    uniq: uniq,
  }

  let newFunding: any = []
  if (fundingsources !== undefined) {
    newFunding = [...fundingsources, obj]
  } else {
    newFunding = [obj]
  }

  let uniqueData = newFunding.filter((value: any, index: any) => {
    const _value = JSON.stringify(value)
    return (
      index ===
      newFunding.findIndex((objt: any) => {
        return JSON.stringify(objt) === _value
      })
    )
  })

  let currentPageData: any = []
  if (fundingsources !== undefined) {
    currentPageData =
      fundingsources &&
      fundingsources?.filter(
        (item: any) => item.type === fields?.fundType && item.amount > 0 && item.Deleted == false,
      )

    currentPageData.push(uniqueData.filter((val: any) => !fundingsources?.includes(val)))
  } else {
    currentPageData.push(newFunding)
  }

  const body = {
    data: {
      fundingsources: [...currentPageData.flat()],
    },
    opportunity_id: cookie.oppID,
    page_name: 'additional_funding',
    step_number: 2,
  }

  try {
    await saveInfo(accessToken, body, request)
    return { fields: result.data, data: obj, status: true }
  } catch (err) {
    return {}
  }
}

export const loader: LoaderFunction = async ({ request }) => {
  await authenticator.isAuthenticated(request, {
    failureRedirect: '/login',
  })
  const session = await sessionStorage.getSession(request.headers.get('cookie'))
  const cookie = (await opportunityId.parse(request.headers.get('Cookie'))) || {}
  if (!cookie?.oppID) return redirect('/dashboard')
  const accessToken = await refreshToken(request, session)
  try {
    const fetchData = await getInfo(accessToken, cookie.oppID, 'additional_funding', request)
    return json(
      {
        fields: {
          fundingsources: fetchData?.data?.response?.additional_funding?.fundingsources,
        },
        dataType: 'loaderData',
      },
      200,
    )
  } catch (err) {
    return null
  }
}

export default function AdditionalScholarshipAndGrants() {
  const loaderActionData = useLoaderActionData<ActionData>()
  const [data, setData] = useState<ActionData>(loaderActionData)
  const navigation = useNavigation()
  const isSubmitting =
    navigation.state === 'submitting' || (navigation.state === 'loading' && !!navigation.formAction)
  const navigate = useNavigate()
  const location = useLocation() as any
  const actionData = useActionData()
  const [showFields, setShowFields] = useState(false)
  const [values, setValues] = useState<object[]>(loaderActionData?.fields?.fundingsources)
  const [additionalFunding, setAdditionalFunding] = useState('')

  const showValueInputs = (e: string) => {
    if (e === 'Yes') {
      setShowFields(true)
    } else {
      return navigate('/id-verification/additionalfundings')
    }
  }
  useEffect(() => {
    if (loaderActionData?.status) {
      if (additionalFunding === '') {
        setAdditionalFunding(' ')
      } else {
        setAdditionalFunding('')
      }
      setShowFields(false)
      if (values) {
        setValues((value) => [...value, ...[actionData?.data]])
      } else {
        setValues([actionData?.data])
      }
    }
  }, [actionData])

  useEffect(() => {
    setData(loaderActionData)
  }, [loaderActionData?.fields])

  return (
    <>
      <Container size="lg" py={50}>
        <Form method="post" onChange={clientValidation(schema, data, setData)} autoComplete="off">
          <Grid gutter="xl">
            <Grid.Col span={{ sm: 12, md: 3 }}>
              <Title className="displayInline" order={2} variant="yellow" fontWeight="700">
                Scholarship and grants
              </Title>
            </Grid.Col>
            <Grid.Col span={{ sm: 12, md: 9 }} className="desktopRightContainer">
              {values &&
              values?.filter(
                (item: any) =>
                  item.type === 'Scholarships and grants' &&
                  item.amount > 0 &&
                  item.Deleted == false,
              ).length > 0 ? (
                <>
                  {values
                    .filter(
                      (elm: any) =>
                        elm.type === 'Scholarships and grants' &&
                        elm.amount > 0 &&
                        elm.Deleted == false,
                    )
                    .map((item: any, index: any) => (
                      <React.Fragment key={index}>
                        {!item.Deleted && (
                          <>
                            <Title
                              className="displayInline"
                              order={6}
                              variant="blue"
                              fontWeight="400"
                            >
                              Funding type: {item.provider}
                            </Title>
                            <TextField
                              id={item.uniq}
                              defaultValue={item.amount}
                              error={data?.errors}
                              type="number"
                              keyPress={preventNonNumericalInput}
                              readOnly={true}
                              showDollar={true}
                              className={'paddingRemove'}
                            />
                          </>
                        )}
                      </React.Fragment>
                    ))}

                  <Title className="displayInline" order={6} variant="blue" fontWeight="400">
                    Do you have additional scholarship and grants to enter?
                  </Title>
                  <SingleSelect
                    error={data?.errors}
                    id="additional"
                    getOptions={() => {
                      return YES_NO_DDN
                    }}
                    updatedValue={showValueInputs}
                    defaultValue={additionalFunding}
                  />
                </>
              ) : null}

              {showFields ||
              values === undefined ||
              (values &&
                values?.filter(
                  (item: any) =>
                    item.type === 'Scholarships and grants' &&
                    item.amount > 0 &&
                    item.Deleted == false,
                ).length === 0) ? (
                <>
                  <Title className="displayInline" order={6} variant="blue" fontWeight="400">
                    Funding type
                  </Title>
                  <SingleSelect
                    id="type"
                    getOptions={() => {
                      return SCHOOL_GOVT_OTHER
                    }}
                    error={data?.errors}
                  />
                  <Title className="displayInline" order={6} fontWeight="400">
                    Amount
                  </Title>
                  <TextField
                    id={'amount'}
                    placeholder={'Enter an amount in U.S. dollars'}
                    error={data?.errors}
                    type="number"
                    showDollar={true}
                    keyPress={preventNonNumericalInput}
                    className={'paddingRemove'}
                  />

                  <TextField
                    id="fundType"
                    defaultValue={'Scholarships and grants'}
                    error={data?.errors}
                    readOnly={true}
                    style={{ display: 'none' }}
                    showDollar={true}
                    className={'paddingRemove'}
                  />

                  <Tip id="tip">
                    Enter the total amount of money you&apos;re receiving for each individual
                    scholarship. For example, if you get $12K a year for two years, enter $24K.
                    Enter scholarships from different sources separately.
                  </Tip>
                </>
              ) : null}
            </Grid.Col>
          </Grid>
          <Space h={30} />
          <FooterButtons
            backButtonId="back"
            id="next"
            outlineButtonOnClick={() => navigate('/id-verification/additionalfundings')}
            showOutlineButton={true}
            showFilledButton={
              showFields ||
              loaderActionData?.fields?.fundingsources === undefined ||
              (loaderActionData?.fields?.fundingsources &&
                loaderActionData?.fields?.fundingsources?.filter(
                  (item: any) =>
                    item.type === 'Scholarships and grants' &&
                    item.amount > 0 &&
                    item.Deleted == false,
                ).length === 0)
                ? true
                : !location?.state?.edit
                ? false
                : null
            }
            uppercase={true}
            filledButtonText="add funding"
            loading={isSubmitting}
            customVariant="secondary"
            footerBack
          />
        </Form>
      </Container>
    </>
  )
}
